import { comparer, reaction } from 'mobx';
import type { DependencyList } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { IUniqueEmailsPayload, Segments } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';

/**
 * Keeps a unique-recipients count in sync with a request payload derived from observable state.
 *
 * buildPayload runs inside a MobX reaction: every observable it reads is tracked, and the count
 * is re-fetched whenever the built payload structurally changes. Return null to report 0 without
 * making a request.
 *
 * Returns undefined while the first request is unresolved and after a failed request, so callers
 * can distinguish "count unknown" from a real count of 0. A refetch keeps reporting the previous
 * count until it settles.
 *
 * The first payload fetches immediately; later payload changes are debounced by
 * DEFAULT_DEBOUNCE_WAIT. Deep observable edits — typing in a group's filter value — fire the
 * reaction per keystroke, and each count request is an expensive cardinality aggregation, so a
 * burst of edits must collapse into a single trailing request.
 *
 * It receives the store's Segments collection — the same one the count is fetched from — so
 * callers can pass it to getSegmentsUniqueEmailsPayload without reaching for useStore themselves.
 *
 * deps must list buildPayload's non-observable closure inputs (e.g. the campaign model). The
 * reaction is subscribed only to observables its previous evaluation read, so swapping to a
 * different model instance is invisible to MobX — only recreating the reaction retargets it.
 */
function useUniqueEmailsCount(
  buildPayload: (Segments: Segments) => IUniqueEmailsPayload | null,
  deps: DependencyList = [],
): number | undefined {
  const { Segments } = useStore();
  const [count, setCount] = useState<number | undefined>(undefined);
  // The reaction outlives renders; the ref keeps it evaluating the latest closure.
  const buildPayloadRef = useRef(buildPayload);
  buildPayloadRef.current = buildPayload;

  useEffect(() => {
    let requestId = 0;
    let fetchTimer: ReturnType<typeof setTimeout> | undefined;
    let hasFetched = false;

    async function getUniqueEmails(payload: IUniqueEmailsPayload): Promise<void> {
      const currentRequestId = ++requestId;
      try {
        const response = await Segments.getUniqueEmailsFromSegments(payload);
        if (response && currentRequestId === requestId) {
          setCount(response.unique_emails);
        }
      } catch (error) {
        // The count is unknown, not 0 — a 0 would read as "no recipients" and lets effects
        // keyed on an empty audience fire off a failed request.
        if (currentRequestId === requestId) {
          setCount(undefined);
        }
      }
    }

    const dispose = reaction(
      (): IUniqueEmailsPayload | null => buildPayloadRef.current(Segments),
      (payload: IUniqueEmailsPayload | null): void => {
        // Each firing supersedes any not-yet-dispatched request for the previous payload.
        clearTimeout(fetchTimer);
        if (payload) {
          if (hasFetched) {
            fetchTimer = setTimeout(() => getUniqueEmails(payload), DEFAULT_DEBOUNCE_WAIT);
          } else {
            hasFetched = true;
            getUniqueEmails(payload);
          }
        } else {
          // Invalidate any in-flight request so its late response can't overwrite the 0.
          requestId += 1;
          setCount(0);
        }
      },
      { equals: comparer.structural, fireImmediately: true },
    );

    return (): void => {
      clearTimeout(fetchTimer);
      requestId += 1;
      dispose();
    };
  }, [Segments, ...deps]);

  return count;
}

export default useUniqueEmailsCount;
